<script setup lang="ts">
  defineOptions({
    name: 'CPPortalSidebarMobileDrawer',
  })

  import type { PropType } from 'vue'
  import { onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPDrawer from '@ui/components/CPDrawer.vue'
  import CPHeaderLite from '@ui/components/CPHeaderLite.vue'
  import CPText from '@ui/components/CPText.vue'

  import { useVibeHost } from '@common/location-layer/composables/useVibeHost'
  import { useSidebarDrawer } from '@common/shared-ui-layer/composables/useSidebarDrawer'

  import CPPortalSidebarNav from './CPPortalSidebarNav.vue'

  import type { PortalNavigationGroup, PortalNavigationItem } from '../types/portal-sidebar'

  defineProps({
    drawerId: {
      type: String,
      default: 'cp-portal-sidebar-drawer',
    },
    drawerWidth: {
      type: [Number, String] as PropType<number | string>,
      default: 280,
    },
    portalName: {
      type: String,
      required: true,
    },
    portalIcon: {
      type: String as PropType<string | null>,
      default: null,
    },
    navigationGroups: {
      type: Array as PropType<PortalNavigationGroup[]>,
      required: true,
    },
    selectedItem: {
      type: Object as PropType<PortalNavigationItem | undefined>,
      default: undefined,
    },
  })

  const { t } = useI18n()
  const { isOpen: sidebarDrawerOpen, close: closeSidebarDrawer } = useSidebarDrawer()
  // On a vibe host the header leaves ClientPortal for the vibe app's home (a full page load).
  const isVibeHost = useVibeHost()

  onUnmounted(() => {
    closeSidebarDrawer()
  })
</script>

<template>
  <CPDrawer
    :id="drawerId"
    v-model:show="sidebarDrawerOpen"
    placement="left"
    :width="drawerWidth"
    :closable="true"
    :header-style="{ padding: '1rem' }"
  >
    <template #header>
      <CPHeaderLite size="lg" @update:close="closeSidebarDrawer">
        <template #header-title>
          <component
            :is="isVibeHost ? 'a' : 'div'"
            :href="isVibeHost ? '/' : undefined"
            class="flex min-w-0 items-center gap-2 no-underline text-inherit hover:no-underline"
          >
            <CPAvatar
              v-if="portalIcon"
              :src="portalIcon"
              :alt="portalName"
              :title="portalName"
              shape="square"
              size="sm"
              class="flex-shrink-0"
            />
            <CPText size="2xl" weight="bold" class="min-w-0 flex-1 truncate text-body">
              {{ isVibeHost ? t('clientportal.navigation.backToHome') : portalName }}
            </CPText>
          </component>
        </template>
      </CPHeaderLite>
    </template>

    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
      <CPPortalSidebarNav
        :portal-name="portalName"
        :navigation-groups="navigationGroups"
        :selected-item="selectedItem"
        :is-collapsed="false"
        @item-click="closeSidebarDrawer"
      />
    </div>

    <!--
      Drawer footer sits below the scrollable body, so this stays pinned to the bottom.
      The entry keeps its own px-4; this override matches portal nav item padding (p-2)
      because the footer and body already share the same 8px horizontal inset.
    -->
    <template v-if="$slots.footer" #footer>
      <div class="w-full [&>#exhibitor-center-mobile-nav-entry]:px-2">
        <slot name="footer" />
      </div>
    </template>
  </CPDrawer>
</template>

<style scoped></style>
